import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, InputLabel, MenuItem, Select, Alert, CircularProgress } from '@mui/material';
import { PermissionRole } from 'src/constants';
import { useState } from 'react';

interface InviteUserDialogProps {
  isOpen: boolean;
  merchantName: string;
  handleClose: () => void;
  handleSubmit: (email: string, role: PermissionRole) => Promise<void>;
}

// Stolen from https://mui.com/material-ui/react-dialog/#form-dialogs
function InviteUserDialog(props: InviteUserDialogProps) {
  const [userRole, setUserRole] = useState<PermissionRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData(event.currentTarget);
      const formJson = Object.fromEntries((formData as any).entries());
      
      if (!userRole) {
        setError("Please select a user role");
        setLoading(false);
        return;
      }
      
      await props.handleSubmit(formJson.email, userRole as PermissionRole);
      setSuccess(true);
      // Close dialog after showing success for a moment
      setTimeout(() => {
        props.handleClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={loading ? undefined : props.handleClose}
      disableEscapeKeyDown
      PaperProps={{
        component: 'form',
        onSubmit: handleFormSubmit,
      }}
    >
      <DialogTitle>Invite User to Merchant Team</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter the email address that you would like to invite to <strong>{props.merchantName}</strong>.
          If they are currently not registered on <strong>Brewprks</strong>, they will receive an invite email.
        </DialogContentText>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 1 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2, mb: 1 }}>
            Invitation sent successfully!
          </Alert>
        )}
        
        <Box display="flex" alignItems="baseline">
          <FormControl required size="small" sx={{ m: 1, minWidth: 120 }} >
            <InputLabel id="limit-select-label">User Role</InputLabel>
            <Select
              labelId="limit-select-label"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value as PermissionRole)}
              label="User Role"
              required
              disabled={loading}
            >
              <MenuItem value={PermissionRole.Staff}>{PermissionRole.Staff}</MenuItem>
              <MenuItem value={PermissionRole.Admin}>{PermissionRole.Admin}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            autoFocus
            required
            margin="dense"
            id="name"
            name="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.handleClose} disabled={loading}>Cancel</Button>
        <Button 
          type="submit" 
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Sending...' : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteUserDialog;
